import React, { useEffect, useState } from 'react';
import { Form, Input, Button, DatePicker, TimePicker, Select, InputNumber, Upload, message, Card, Spin, Alert } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { clearUsersList, getUsersList } from '../../store/slices/userSlice';
import { createJob, getSlotAvailability } from '../../store/slices/jobSlice';
import { useCategories } from '../../hooks/useCategories';
import { fetchSubCategories, fetchSubServices } from '../../store/slices/subCategorySlice';
import { fetchServices } from '../../store/slices/servicesSlice';

const AddJob = () => {
    const [form] = Form.useForm();
    const servicesWatch = Form.useWatch("services", form);
    const dispatch = useDispatch()
    const [buttonLoading, setButtonLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("success");
    const [fileList, setFileList] = useState([]);
    const [subCategoriesPerRow, setSubCategoriesPerRow] = useState({});
    const [servicesPerRow, setServicesPerRow] = useState({});
    const [subservicesPerRow, setSubServicesPerRow] = useState({});
    const { usersList, loading } = useSelector((state) => state?.user)
    const { categories, error, fetchCategories } = useCategories();
    const { services } = useSelector((state) => state.services);
    const [timeSlots, setTimeSlots] = useState([]);
    const [slotLoading, setSlotLoading] = useState(false);
    const [servicePrices, setServicePrices] = useState({});
    // console.log(usersList)

    useEffect(() => {
        fetchCategories();
    }, []);
    const handleSearch = (value) => {
        if (value.length == 0) {
            dispatch(clearUsersList())
        }
        if (value.length < 3) return;
        dispatch(getUsersList({ roleType: "user", search: value }));
    };

    const onClear = () => {
        dispatch(clearUsersList());
    }

    const handleChange = (info) => {
        setFileList(info.fileList);
    };

    const beforeUpload = (file) => {
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
        message.error('You can only upload image files!');
        return Upload.LIST_IGNORE;
        }
        return false; // prevent auto upload
    };

    const handleFinish = async (values) => {
        setButtonLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            const formData = new FormData();
            // Basic fields
            formData.append("user_id", values.user_id);
            formData.append("job_date", values.job_date.format("YYYY-MM-DD"));
            formData.append("time_slot", values.time_slot);
            formData.append("payment_method", values.payment_method);
            formData.append("categorization", values.categorization);
            formData.append("details", values.details);
            // formData.append("cost", values.cost);
            formData.append("cost", calculateTotalCost());

            // Delivery Address (nested object)
            formData.append("delivery_address[full_name]", values.full_name);
            formData.append("delivery_address[phone]", values.phone);
            formData.append("delivery_address[region]", values.region);
            formData.append("delivery_address[city]", values.city);
            formData.append("delivery_address[area_code]", values.area_code);
            formData.append("delivery_address[address]", values.address);
            formData.append("delivery_address[label]", values.label);
            formData.append("delivery_address[default_address]", "1");

            // Services (array of objects)
            values.services.forEach((service, index) => {
                formData.append(`services[${index}][category]`, service.category);
                formData.append(`services[${index}][subCategory]`, service.subCategory);
                formData.append(`services[${index}][service_type]`, service.service_type);
                formData.append(`services[${index}][sub_service]`, service.sub_service);
                formData.append(`services[${index}][quantity]`, service.quantity);
            });

            // Images
            fileList.forEach((file) => {
                formData.append("images", file.originFileObj);
            });
            const resultAction = await dispatch(createJob(formData))
            if (createJob.fulfilled.match(resultAction)) {
                setAlertMessage(resultAction?.payload?.message || 'Job created successfully');
                setAlertType('success');
                form.resetFields();
                setFileList([]);
            } else {
                console.log(resultAction?.payload)
                setAlertMessage(resultAction?.payload || 'Error creating job');
                setAlertType('error');
            }
        } catch (error) {
            message.error('Failed to submit job form.');
        } finally {
            setButtonLoading(false);
        }
    };

    const handleCategoryChange = (categoryId, rowIndex) => {
        dispatch(fetchSubCategories(categoryId)).then((res) => {
          setSubCategoriesPerRow((prev) => ({
            ...prev,
            [rowIndex]: res.payload
          }));
          setServicesPerRow((prev) => ({
            ...prev,
            [rowIndex]: []
          }));
        });
    };

    const handleSubCategoryChange = (subCategoryId, rowIndex, categoryId) => {
        dispatch(fetchServices({ categoryId, subCategoryId })).then((res) => {
          setServicesPerRow((prev) => ({
            ...prev,
            [rowIndex]: res.payload.data
          }));
        });
    };

    const handleJobDateChange = async (date) => {
        if (!date) return;
    
        const dayName = date.format('ddd');
    
        try {
            setSlotLoading(true);
    
            const res = await dispatch(
                getSlotAvailability({
                    day: dayName,
                })
            );
    
            setTimeSlots(res?.payload?.data || []);
            console.log(timeSlots, 'timeSlots')
    
            // reset previously selected slot
            form.setFieldsValue({ time_slot: undefined });
    
        } catch (err) {
            console.log(err)
            setAlertMessage('Failed to load time slots');
            setAlertType('error');
        } finally {
            setSlotLoading(false);
        }
    };

    const calculateRowTotal = (index) => {
        const qty = servicesWatch?.[index]?.quantity || 0;
        const price = servicePrices[index] || 0;
        console.log(qty, servicePrices)
        return qty * price;
    };

    const calculateTotalCost = () => {
        if (!servicesWatch) return 0;
        return servicesWatch.reduce((sum, _, index) => {
            const qty = servicesWatch[index]?.quantity || 0;
            const price = servicePrices[index] || 0;
            return sum + qty * price;
        }, 0);
    };

    return (
        <Card title="Create Job" bordered={false}>
            {alertMessage && (
                <Alert
                    message={alertMessage}
                    type={alertType}
                    showIcon
                    icon={alertType === 'success' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                    closable
                    onClose={() => setAlertMessage('')}
                    style={{
                        marginBottom: 24,
                        borderRadius: '8px',
                    }}
                />
            )}
            <Form
                layout="vertical"
                form={form}
                onFinish={handleFinish}
                initialValues={{
                    services: [{}]  // 👈 One row by default
                }}
            >
                <Form.Item
                    label="User"
                    name="user_id"
                    rules={[{ required: true, message: 'Please enter user ID' }]}
                >
                    <Select
                    showSearch
                    allowClear
                    placeholder="Type at least 3 characters to search"
                    filterOption={false} // disable local filtering
                    onSearch={handleSearch} // called when user types
                    notFoundContent={loading ? <Spin size="small" /> : null}
                    onClear={onClear}
                    options={usersList?.data?.map((data) => ({
                        value: data._id,
                        label: `${data.first_name} ${data?.last_name}`,
                    }))}
                    />
                </Form.Item>

                <Form.Item
                    label="Job Date"
                    name="job_date"
                    rules={[{ required: true, message: 'Please select job date' }]}
                >
                    <DatePicker style={{ width: '100%' }} onChange={handleJobDateChange} />
                </Form.Item>

                {/* <Form.Item
                    label="Job Start Time"
                    name="job_start_time"
                    rules={[{ required: true, message: 'Please select start time' }]}
                >
                    <TimePicker style={{ width: '100%' }} format="HH:mm" />
                </Form.Item>

                <Form.Item
                    label="Job End Time"
                    name="job_end_time"
                    rules={[{ required: true, message: 'Please select end time' }]}
                >
                    <TimePicker style={{ width: '100%' }} format="HH:mm" />
                </Form.Item> */}

                <Form.Item
                    label="Time Slot"
                    name="time_slot"
                    rules={[{ required: true, message: 'Please select time slot' }]}
                >
                    <Select
                    placeholder="Select time slot"
                    // options={[
                    //     { value: 'Morning', label: 'Morning' },
                    //     { value: 'Afternoon', label: 'Afternoon' },
                    //     { value: 'Evening', label: 'Evening' },
                    // ]}
                    options={timeSlots?.[0]?.time_slot?.map((data) => ({
                        value: `${data.start_time}-${data.end_time}`,
                        label: `${data.start_time} - ${data.end_time}`,
                    }))}
                    />
                </Form.Item>

                <Form.Item
                    label="Payment Method"
                    name="payment_method"
                    rules={[{ required: true, message: 'Please select payment method' }]}
                >
                    <Select
                    placeholder="Select payment method"
                    options={[
                        { value: 'card', label: 'Card' },
                        { value: 'cash', label: 'Cash' },
                    ]}
                    />
                </Form.Item>

                <Form.Item
                    label="Categorization"
                    name="categorization"
                    rules={[{ required: true, message: 'Please select categorization' }]}
                >
                    <Select
                    placeholder="Select time categorization"
                    options={[
                        { value: 'simple', label: 'Simple' },
                        { value: 'complex', label: 'Complex' },
                    ]}
                    />
                </Form.Item>

                {/* <Form.Item
                    label="Service Type"
                    name="service"
                    rules={[{ required: true, message: "Select service type" }]}
                >
                    <Select
                    placeholder="Select category"
                    options={[
                        {
                            value: 'simple',
                            label : 'Simple'
                        },
                        {
                            value: 'complex',
                            label : 'Complex'
                        }
                    ]}
                    />
                </Form.Item> */}

                <Form.List name="services">
                {(fields, { add, remove }) => (
                    <>
                    {fields.map((field, index) => (
                        <Card
                        key={field.key}
                        style={{ marginBottom: 20, background: "#fafafa" }}
                        >
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <h4>Service #{index + 1}</h4>
                            {index > 0 && (
                            <Button danger onClick={() => remove(field.name)}>
                                Remove
                            </Button>
                            )}
                        </div>

                        {/* CATEGORY */}
                        <Form.Item
                            {...field}
                            label="Category"
                            name={[field.name, "category"]}
                            fieldKey={[field.fieldKey, "category"]}
                            rules={[{ required: true, message: "Select category" }]}
                        >
                            <Select
                            placeholder="Select category"
                            options={categories?.map((cat) => ({
                                key : cat?._id,
                                value: cat._id,
                                label: cat.name,
                            }))}
                            onChange={(catId) => handleCategoryChange(catId, index)}
                            />
                        </Form.Item>

                        {/* SUB CATEGORY */}
                        <Form.Item
                            {...field}
                            label="Sub Category"
                            name={[field.name, "subCategory"]}
                            fieldKey={[field.fieldKey, "subCategory"]}
                            rules={[{ required: true, message: "Select sub category" }]}
                        >
                            <Select
                            placeholder="Select sub category"
                            options={(subCategoriesPerRow[index] || []).map((sub) => ({
                                key : sub._id,
                                value: sub._id,
                                label: sub.name,
                            }))}
                            onChange={(subId) =>
                                handleSubCategoryChange(subId, index, form.getFieldValue(["services", index, "category"]))
                            }
                            disabled={!subCategoriesPerRow[index]?.length}
                            />
                        </Form.Item>

                        {/* SERVICE TYPE */}
                        <Form.Item
                            {...field}
                            label="Service"
                            name={[field.name, "service_type"]}
                            fieldKey={[field.fieldKey, "service_type"]}
                            rules={[{ required: true, message: "Select service" }]}
                        >
                            <Select
                            placeholder="Select service"
                            options={(servicesPerRow[index] || []).map((s) => ({
                                value: s._id,
                                label: s.title,
                                price: s.price,
                            }))}
                            onChange={(value, option) => {
                                const service = servicesPerRow[index].find(s => s._id === value);
                                setServicePrices(prev => ({
                                    ...prev,
                                    [index]: service?.price || 0
                                }));
                                dispatch(fetchSubServices(service?._id)).then((res) => {
                                    setSubServicesPerRow((prev) => ({
                                        ...prev,
                                        [index]: res.payload
                                    }));
                                    // setServicesPerRow((prev) => ({
                                    //     ...prev,
                                    //     [index]: []
                                    // }));
                                });
                            }}
                            disabled={!servicesPerRow[index]?.length}
                            />
                        </Form.Item>

                        {/* SUB SERVICE */}
                        <Form.Item
                            {...field}
                            label="Sub Service"
                            name={[field.name, "sub_service"]}
                            fieldKey={[field.fieldKey, "sub_service"]}
                            rules={[{ required: true, message: "Select sub service" }]}
                        >
                            <Select
                            placeholder="Select sub service"
                            options={(subservicesPerRow[index] || []).map((s) => ({
                                value: s._id,
                                label: s.name,
                                price: s.cost,
                            }))}
                            // onChange={(value, option) => {
                            //     const service = subservicesPerRow[index].find(s => s._id === value);
                            //     setServicePrices(prev => ({
                            //         ...prev,
                            //         [index]: service?.price || 0
                            //     }));
                            // }}
                            disabled={!subservicesPerRow[index]?.length}
                            />
                        </Form.Item>
                        <Form.Item
                            {...field}
                            label="Quantity"
                            name={[field.name, "quantity"]}
                            fieldKey={[field.fieldKey, "quantity"]}
                            rules={[{ required: true, message: 'Please input quantity!' }]}
                        >
                            <InputNumber placeholder="Enter quantity" style={{ width : "100%" }} />
                        </Form.Item>
                        Sub Total: ${calculateRowTotal(index)}
                        </Card>
                    ))}

                    <Button type="dashed" onClick={() => add()} block>
                        + Add Another Service
                    </Button>
                    </>
                )}
                </Form.List>

                <Form.Item
                    label="Details"
                    name="details"
                    rules={[{ required: true, message: 'Please enter details' }]}
                >
                    <Input.TextArea placeholder="Enter job details" rows={4} />
                </Form.Item>

                <Form.Item
                    label="Full Name"
                    name="full_name"
                    rules={[{ required: true, message: 'Please enter full name' }]}
                >
                    <Input placeholder="Enter full name" />
                </Form.Item>

                <Form.Item
                    label="Phone"
                    name="phone"
                    rules={[{ required: true, message: 'Please enter phone' }]}
                >
                    <Input placeholder="Enter phone" />
                </Form.Item>

                <Form.Item
                    label="Region"
                    name="region"
                    rules={[{ required: true, message: 'Please enter region' }]}
                >
                    <Input placeholder="Enter region" />
                </Form.Item>

                <Form.Item
                    label="City"
                    name="city"
                    rules={[{ required: true, message: 'Please enter city' }]}
                >
                    <Input placeholder="Enter city" />
                </Form.Item>

                <Form.Item
                    label="Area Code"
                    name="area_code"
                    rules={[{ required: true, message: 'Please enter area code' }]}
                >
                    <Input placeholder="Enter area code" />
                </Form.Item>

                <Form.Item
                    label="Address"
                    name="address"
                    rules={[{ required: true, message: 'Please enter address' }]}
                >
                    <Input.TextArea placeholder="Enter address" rows={4} />
                </Form.Item>

                <Form.Item
                    label="Label"
                    name="label"
                    rules={[{ required: true, message: 'Please enter label' }]}
                >
                    <Input placeholder="Enter label" />
                </Form.Item>

                {/* <Form.Item
                    label="Cost"
                    name="cost"
                    rules={[{ required: true, message: 'Please enter cost' }]}
                >
                    <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    step={1}
                    value={calculateTotalCost()}
                    disabled
                    placeholder="Enter cost"
                    prefix="$"
                    />
                </Form.Item> */}

                <Form.Item label="Images" name="images"  rules={[{ required: true, message: 'Please upload atleast one image' }]}>
                    <Upload
                    beforeUpload={beforeUpload}
                    fileList={fileList}
                    onChange={handleChange}
                    accept="image/*"
                    multiple
                    listType="picture"
                    >
                    <Button icon={<UploadOutlined />}>Select Images</Button>
                    </Upload>
                </Form.Item>

                <Form.Item>
                    <Button
                    type="primary"
                    htmlType="submit"
                    loading={buttonLoading}
                    style={{ marginTop: 16 }}
                    >
                    Submit
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};

export default AddJob;